import React, { useState, useRef, useLayoutEffect } from 'react';
import { T } from './tokens';

// One record on a phone — shared by BOTH data tables.
//
// There are two AoneDataTable render paths and the barrel exports the Fiori
// one, so a card written into design-system/components.js alone never reaches
// a screen. That trap has already cost this codebase days twice (the /oms
// cancelled-row tint, and a required-marker fix that deployed green and
// changed nothing). The card lives here so components.js and fiori.js import
// the same thing and cannot drift apart again.
//
// The old mobile rendering transposed the table — every column became a
// LABEL … value line, so an eight-column table became an eight-line card and
// the reader had to hunt for the one number they came for. That is why 45
// hand-built Mobile*.js pages existed alongside it: the shared table could not
// make a phone screen readable, so every module wrote its own.
//
// A card reads top-left first, then right. So: the name, the number that
// matters, the state, and one supporting line. Everything else is real but
// secondary and sits behind a disclosure. Columns say which is which:
//
//   mobilePrimary   the card's title            (default: first text column)
//   mobileMetric    the big right-hand figure   (default: first numeric)
//   mobileStatus    the chip beside the meta    (default: a column keyed status)
//   mobileMeta      the supporting line         (default: next two columns)
//   mobileHide      not on a phone at all
//
// Every hint is optional and the defaults are derived, so a page passing plain
// columns gets the card without changing a line.

const isActionsCol = (c) => c.key === 'actions' || c.key === '_actions';

// A reference identifies a record; a name says what it IS. Taking the first
// text column as the title made "#r0" the headline of a support ticket while
// its subject sat underneath as a labelled meta line — the transposed-table
// rendering this card exists to replace, arrived at from the other direction.
// A reference is still worth showing, so it becomes the caption under the
// title, which is where Purchase Orders puts its PO number by hand.
// A reference: what you quote on the phone, not what the record is.
// Three spellings, because all three are in this codebase:
//   snake/lower  poNumber, so_no, ref, code
//   camelCase    courierId, quotationId  — tested case-SENSITIVELY, since a
//                case-insensitive /id$/ also matches "valid", "paid", "void"
//   the label    a column headed "Doc", "Ref" or "No." is a reference however
//                its key is spelled — Courier's `courierId` is headed "Doc"
// The bare-code names are matched WHOLE, not as a prefix. `^sku` also matches
// `skuName`, which is the product's name and the best title a stock row has —
// the warehouse register headlined every card with an em dash because of it.
// Longer forms are already covered by the suffix rules: batchCode ends in
// `code`, awbNumber and gstNumber in `number`.
const IDENTIFIES = /(^|_)(id|ref|code)$|number$|no$|num$|code$|^(sku|batch|awb|gst|hsn)$/i;
const IDENTIFIES_CAMEL = /[a-z](Id|Ref|Code|No)$/;
const IDENTIFIES_LABEL = /^(doc|ref|no\.?|id|code|number|#)$/i;
const identifies = (c) => {
  const key = c.key || '';
  // The KEY outranks the LABEL. A label is a human caption and gets
  // abbreviated — the warehouse register heads `skuName` as plain "SKU" — but
  // the key says what the value is. If the key names the thing, a label that
  // looks like a reference does not make it one.
  if (NAMES_SELF.test(key) && !IDENTIFIES.test(key) && !IDENTIFIES_CAMEL.test(key)) return false;
  return (
    IDENTIFIES.test(key)
    || IDENTIFIES_CAMEL.test(key)
    || IDENTIFIES.test(String(c.label || ''))
    || IDENTIFIES_LABEL.test(String(c.label || '').trim())
  );
};
// What the record IS beats who it is FOR. A support ticket's headline is its
// subject; the client is context. Both match "a name", so they are ranked:
// the record's own name first, a related party second.
const NAMES_SELF = /name|title|subject|item|product|material|description|label|formulation|campaign|task/i;
// A PLURAL of one of those is a count of children, not the record's name — an
// order's "Products" column renders "4 products", and taking it as the title
// gave every dispatch card the headline "0 products" with the SO number
// demoted to a caption underneath.
//
// Matched on the key AND the label, and anchored at the END so camelCase
// counts too: `lineItems` is a count of lines however it is spelled, and
// testing the bare key alone let it through to headline every sales order
// "0 lines". `productName` and `skuName` end in "name" and are unaffected.
const COLLECTION = /(lines|items|products|materials|skus|labels|tasks|orders|batches|attachments|documents|photos|files)$/i;
const NAMES_PARTY = /client|customer|vendor|supplier|company|employee|creator|assign|owner/i;
const looksLike = (re, c) => re.test(c.key || '') || re.test(String(c.label || ''));

/** Resolve the card's slots from a column list. */
export function pickCardFields(columns, { colLabel, colValue }) {
  const visible = columns.filter((c) => !c.mobileHide && !isActionsCol(c));
  const actionsCol = columns.find((c) => isActionsCol(c) && !c.mobileHide);
  const pick = (flag, fallback) => visible.find((c) => c[flag]) || fallback();

  const text = visible.filter((c) => !c.numeric && c.key !== 'status');
  const isCollection = (c) => looksLike(COLLECTION, c);
  const named = (re) => text.find((c) => (
    looksLike(re, c) && !identifies(c) && !isCollection(c)
  ));
  const primary = pick('mobilePrimary', () => (
    named(NAMES_SELF)
    || named(NAMES_PARTY)
    || text.find((c) => !identifies(c))
    || text[0]
    || visible[0]
  ));
  // Only the reference the title displaced, and only when it was displaced —
  // a page whose title already IS its reference gets no caption repeating it.
  const caption = pick('mobileCaption', () => (
    primary && !identifies(primary) ? text.find(identifies) : null
  ));
  const metric = pick('mobileMetric', () => visible.find((c) => c.numeric && c !== primary));
  const status = pick('mobileStatus', () => visible.find((c) => c.key === 'status' && c !== primary));
  const taken = (c) => c === primary || c === caption || c === metric || c === status;
  const explicitMeta = visible.filter((c) => c.mobileMeta);
  const meta = explicitMeta.length ? explicitMeta : visible.filter((c) => !taken(c)).slice(0, 2);
  const rest = visible.filter((c) => !taken(c) && !meta.includes(c));

  const cell = (col, row) => (col ? (col.render ? col.render(colValue(col, row), row) : (colValue(col, row) ?? '—')) : null);

  return (row) => ({
    primary: cell(primary, row),
    caption: caption ? cell(caption, row) : null,
    metric: metric ? cell(metric, row) : null,
    metricLabel: metric ? colLabel(metric) : null,
    status: status ? cell(status, row) : null,
    meta: meta.map((c) => ({ key: c.key, label: colLabel(c), value: cell(c, row) })),
    rest: rest.map((c) => ({ key: c.key, label: colLabel(c), value: cell(c, row), numeric: c.numeric })),
    actions: actionsCol ? cell(actionsCol, row) : null,
  });
}

export function MobileRecordCard({
  primary, caption, metric, metricLabel, status, meta = [], rest = [], actions,
  selected, accent, onOpen, extraStyle, lead,
}) {
  const [open, setOpen] = useState(false);
  const hasRest = rest.length > 0;
  // A column whose render says "no value" must not become a meta line — an
  // `Expected —` reads as a fact and is the absence of one. The value can be a
  // string, or an element tree with the dash somewhere inside it, so pull the
  // text out and judge that.
  const textOf = (v) => {
    if (v == null || typeof v === 'boolean') return '';
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (Array.isArray(v)) return v.map(textOf).join('');
    return textOf(v?.props?.children);
  };
  const isBlank = (t) => {
    const s = String(t).replace(/[\s·]/g, '');
    return s === '' || s === '—' || s === '-' || s === '–';
  };
  // textOf can only read PLAIN elements. A value produced by a component —
  // <RatePerUom rate={95.5} />, <InwardAlertChip .../> — keeps its text inside
  // its own render, with no `children` prop to walk, so textOf returns '' for
  // it. Read as "empty", that silently deleted the line: the RM inward card
  // lost the rate per kg, the number the whole screen exists to record.
  // Guessing from the element tree cannot settle this in either direction —
  // so don't guess. Render the line, then read back what it actually says and
  // hide it if the answer is a dash. `hideBlank` below does that; this test
  // only catches what is provably empty before render.
  const emptyish = (v) => isBlank(textOf(v));
  // Two columns often resolve to the same string — a ticket's Client and its
  // Title both fall back to the customer name, a row's Item and Material both
  // read the master. Printing it twice on a two-line card costs the line that
  // would have said something new.
  // A record whose name is missing — an order with no client on it — headlined
  // a bare em dash while its reference sat underneath in small grey type. The
  // reference is the better title when there is nothing else; nothing is worse
  // than a dash where the name goes.
  const title = emptyish(primary) && !emptyish(caption) ? caption : primary;
  const sub = title === caption ? null : caption;

  const seen = new Set([textOf(title).trim(), textOf(sub).trim()].filter(Boolean));
  // A status column with nothing to say renders its own placeholder dash. In a
  // chip slot that reads as a state the record is in, rather than the absence
  // of one — so drop it the way an empty meta line is dropped.
  // The two passes split the work by what each can actually know.
  //
  // Here, before render, we can only judge a value textOf could READ — a
  // string, a number, a tree of plain elements. An empty reading is not a
  // verdict: <RatePerUom rate={140} /> reads as '' because its text lives
  // inside its own render, and dropping it on that basis deleted the rate per
  // kg from every RM inward card. So an unreadable value passes through here
  // and is settled below, against what it actually put on screen.
  const has = (v) => v != null && v !== false && v !== '';
  const keep = (v) => {
    const t = textOf(v).trim();
    if (!t) return has(v);          // unreadable — let the rendered text decide
    return !isBlank(t);
  };

  const shownStatus = keep(status) ? status : null;
  const shownMeta = meta.filter((m) => {
    if (!keep(m.value)) return false;
    const t = textOf(m.value).trim();
    if (!t) return true;            // unreadable — cannot dedupe on it either
    if (seen.has(t)) return false;
    seen.add(t);
    return true;
  });

  // And here, after render, we can read exactly what the reader sees. A
  // component that renders its own placeholder dash when it has nothing to
  // say — InwardAlertChip, RatePerUom, the attention-queue age cell — puts a
  // bare em dash in a slot that reads as a fact about the record rather than
  // the absence of one. Hide those.
  const metaRowRef = useRef(null);
  useLayoutEffect(() => {
    const root = metaRowRef.current;
    if (!root) return;
    for (const el of root.querySelectorAll('[data-blankable]')) {
      el.hidden = isBlank(el.textContent || '');
    }
  });

  return (
    <div
      onClick={onOpen || undefined}
      style={{
        background: T.surface,
        borderRadius: T.r.lg,
        border: `1px solid ${selected ? (accent || T.accent) : T.border}`,
        boxShadow: T.shadow.sm,
        // 12px keeps the card over the 44px tap target on one line of content
        // while fitting roughly twice as many records on a phone screen as the
        // label-per-column rendering did.
        padding: '12px 14px',
        cursor: onOpen ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        fontFamily: T.font,
        ...(extraStyle || {}),
      }}
    >
      {lead}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: T.type.md, fontWeight: 660, color: T.text, overflowWrap: 'anywhere', lineHeight: 1.3 }}>
            {title}
          </div>
          {!emptyish(sub) && (
            <div style={{ fontSize: T.type.xs, color: T.textMuted, marginTop: 2, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
              {sub}
            </div>
          )}
        </div>
        {metric != null && metric !== '' && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {/* Caption above the figure. A metric column's render often adds
                its own lines — a reorder note, a tick — and a caption placed
                underneath ends up third, describing whatever landed last. */}
            {metricLabel && (
              <div style={{ fontSize: T.type.xxs, color: T.textTer, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 1 }}>
                {metricLabel}
              </div>
            )}
            <div style={{ fontSize: T.type.lg, fontWeight: 760, color: T.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
              {metric}
            </div>
          </div>
        )}
      </div>

      {/* One line under the title carries the state, the supporting facts and
          the action. Giving each of those its own row turned a list you scan
          into three screens of cards; this keeps a record to two lines. */}
      {(shownStatus || shownMeta.length > 0 || actions || hasRest) && (
        <div ref={metaRowRef} style={{ display: 'flex', alignItems: 'center', gap: '6px 12px', flexWrap: 'wrap', marginTop: 8 }}>
          {shownStatus && <span data-blankable="">{shownStatus}</span>}
          {shownMeta.map((m, mi) => (
            <span key={mi} data-blankable="" style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, minWidth: 0, fontSize: T.type.xs }}>
              <span style={{ color: T.textTer }}>{m.label}</span>
              <span style={{ color: T.textSec, fontWeight: 600, overflowWrap: 'anywhere' }}>{m.value}</span>
            </span>
          ))}
          <span style={{ flex: 1, minWidth: 0 }} />
          {hasRest && (
            <button
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
              style={{
                border: 'none', background: 'transparent', padding: '4px 0', cursor: 'pointer',
                fontSize: T.type.xs, fontWeight: 700, color: T.textSec, fontFamily: T.font, flexShrink: 0,
              }}
            >{open ? 'Less' : `+${rest.length}`}</button>
          )}
          {actions && <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>{actions}</span>}
        </div>
      )}

      {open && hasRest && (
        <div style={{ marginTop: 6, borderTop: `1px dashed ${T.divider}`, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rest.map((r, ri) => (
            <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: T.type.xs }}>
              <span style={{ color: T.textTer, fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 }}>{r.label}</span>
              <span style={{ color: T.text, fontWeight: 560, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere', fontVariantNumeric: r.numeric ? 'tabular-nums' : undefined }}>
                {r.value ?? '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
